/** biome-ignore-all lint/correctness/useHookAtTopLevel: We need to break out early if not on production environment  */
import { useRouterSafe } from '@/hooks/navigation/useRouterSafe';
import { useEffect, useState } from 'react';
import Clarity from '@microsoft/clarity';
import Cookies from 'js-cookie';
import { useSettings } from '@/hooks/state/useSettings';
import { useSubscriptionType, useIsAdmin } from '@/hooks/auth';
import { getSessionId } from '@/utils/tracking';
import { getDeviceId } from '@/providers/ABProvider/getUUID';
const CLARITY_PROJECT_ID = 'rgcj1kb9sw';

// Helper function to safely set Clarity tags
const setTag = (tagName: string, value: string) => {
  try {
    Clarity.setTag(tagName, value);
  } catch {
    // Tag setting failed, but don't break the app
  }
};
export const MicrosoftClarity = () => {
  const [initialized, setInitialized] = useState(false);
  const [consentGranted, setConsentGranted] = useState(false);
  const {
    asPath,
    isReady
  } = useRouterSafe();
  const {
    settings
  } = useSettings();
  const subscriptionType = useSubscriptionType();
  const isAdmin = useIsAdmin();

  // Only run in production environment
  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    return null;
  }

  // Initialize Clarity
  useEffect(() => {
    if (initialized) {
      return;
    }
    try {
      Clarity.init(CLARITY_PROJECT_ID);
      setInitialized(true);
    } catch {
      // Initialization failed, but don't break the app
    }
  }, [initialized]);

  // Handle cookie consent
  useEffect(() => {
    if (!initialized) {
      return;
    }

    // Auto-reject cookies for admin users
    if (isAdmin) {
      setConsentGranted(false);
      Clarity.consent(false);
      return;
    }
    const onAccept = () => {
      if (window.Cookiebot?.consent?.marketing) {
        setConsentGranted(true);
        Clarity.consent(true);
      }
    };
    const onDecline = () => {
      if (!window.Cookiebot?.consent?.marketing) {
        setConsentGranted(false);
        Clarity.consent(false);
      }
    };

    // Check initial consent state
    if (typeof window !== 'undefined' && window.Cookiebot) {
      if (window.Cookiebot?.consent?.marketing) {
        setConsentGranted(true);
        Clarity.consent(true);
      } else {
        setConsentGranted(false);
        Clarity.consent(false);
      }
    }

    // Listen for consent changes
    window.addEventListener('CookiebotOnAccept', onAccept);
    window.addEventListener('CookiebotOnDecline', onDecline);
    return () => {
      window.removeEventListener('CookiebotOnAccept', onAccept);
      window.removeEventListener('CookiebotOnDecline', onDecline);
    };
  }, [initialized, isAdmin]);

  // Handle user identification
  useEffect(() => {
    if (!(initialized && consentGranted && isReady)) {
      return;
    }
    const user = settings?.user;

    // Use user ID as primary identifier, fallback to device ID for anonymous users
    const customId = user?.id ? user.id.toString() : getDeviceId();
    if (customId) {
      try {
        Clarity.identify(customId,
        // Required: custom user ID or device ID
        getSessionId() || undefined,
        // Optional: custom session ID (same as ClickHouse)
        asPath,
        // Optional: custom page ID
        user?.first_name || undefined // Optional: friendly name
        );
      } catch {
        // User identification failed, but don't break the app
      }
    }
  }, [initialized, consentGranted, settings?.user, asPath, isReady]);

  // Re-identify user on page changes (recommended by Clarity docs)
  useEffect(() => {
    if (!(initialized && consentGranted && isReady)) {
      return;
    }
    const user = settings?.user;

    // Use user ID as primary identifier, fallback to device ID for anonymous users
    const customId = user?.id ? user.id.toString() : getDeviceId();
    if (customId) {
      try {
        Clarity.identify(customId, getSessionId() || undefined,
        // Use same session ID as ClickHouse
        asPath, user?.email || user?.first_name || undefined);
      } catch {
        // Re-identification failed, but don't break the app
      }
    }
  }, [initialized, consentGranted, asPath, isReady, settings?.user]);

  // Handle comprehensive UTM parameter tracking
  useEffect(() => {
    if (!(initialized && consentGranted)) {
      return;
    }

    // Define UTM parameters to track (matching middleware UTM params)
    const utmParams = [{
      cookie: 'utm_source',
      tag: 'source'
    }, {
      cookie: 'utm_medium',
      tag: 'medium'
    }, {
      cookie: 'utm_campaign',
      tag: 'campaign'
    }, {
      cookie: 'utm_term',
      tag: 'term'
    }, {
      cookie: 'utm_content',
      tag: 'content'
    }];

    // Set tags for all available UTM parameters
    for (const {
      cookie,
      tag
    } of utmParams) {
      const value = Cookies.get(cookie);
      if (value) {
        setTag(tag, value);
      }
    }
  }, [initialized, consentGranted]);

  // Handle user context tags
  useEffect(() => {
    if (!(initialized && consentGranted)) {
      return;
    }
    const user = settings?.user;

    // Set authentication status tag
    setTag('auth_status', user?.is_authenticated ? 'authenticated' : 'anonymous');

    // Set email verification status
    if (user?.isEmailVerified !== undefined) {
      setTag('email_verified', user.isEmailVerified ? 'true' : 'false');
    }

    // Set country tag
    if (user?.country) {
      setTag('country', user.country);
    }

    // Set Google user status
    if (user?.isGoogleUser !== undefined) {
      setTag('google_user', user.isGoogleUser ? 'true' : 'false');
    }
  }, [initialized, consentGranted, settings?.user]);

  // Handle subscription tier tag separately as it changes independently
  useEffect(() => {
    if (!(initialized && consentGranted)) {
      return;
    }
    setTag('subscription_tier', subscriptionType);
  }, [initialized, consentGranted, subscriptionType]);

  // This component doesn't render anything visible
  return null;
};